import { QuestionProps, QuestionLogicRule } from "../Question";
import { LeadQuizFlowQuestions } from "./LeadQuizFlowQuestions";

/**
 * Questions that should be omitted (not shown) in the quiz flow.
 */
export const OMIT_QUESTIONS = ["wedding_vibe", "decor_type", "decor_detail"];

/**
 * Finds the next index based on logic and current answers.
 * Supports optional dependency on another question's answer via `requiredAnswerIndex`.
 */
export const getNextIndexFromLogic = (
    logic: QuestionLogicRule[] | undefined,
    currentAnswer: any,
    allQuestions: QuestionProps[]
): string | null => {
    if (!logic) return null;

    let indexToGo: string | null = null;

    for (const rule of logic) {
        if (rule.requiredAnswerIndex) {
            const required = allQuestions.find(q => q.index === rule.requiredAnswerIndex);
            if (required?.answer === rule.value) {
                indexToGo = rule.nextIndex;
            }
        } else if (typeof rule.value !== "undefined") {
            if (currentAnswer === rule.value) {
                indexToGo = rule.nextIndex;
            }
        } else {
            indexToGo = rule.nextIndex;
        }
    }

    if (indexToGo && OMIT_QUESTIONS.includes(indexToGo)) {
        const omittedQuestion = LeadQuizFlowQuestions.find(q => q.index === indexToGo);
        if (omittedQuestion?.logic) {
            getNextIndexFromLogic(omittedQuestion.logic, omittedQuestion.answer, allQuestions);
        }
    }

    return indexToGo;
};

/**
 * Retrieves the next valid question (skipping omitted questions) from the schema.
 * Follows logic recursively until a non-omitted question is found.
 */
export const getNextQuestionFromSchema = (
    startIndex: string,
    allSchemaQuestions: QuestionProps[]
): QuestionProps | null => {
    let next = startIndex;

    while (OMIT_QUESTIONS.includes(next)) {
        const skipped = allSchemaQuestions.find(q => q.index === next);
        next = getNextIndexFromLogic(skipped?.logic, skipped?.answer ?? "", allSchemaQuestions) ?? "";
        if (!next) return null;
    }

    const nextQuestion = allSchemaQuestions.find(q => q.index === next);
    if (!nextQuestion) return null;

    return {
        ...nextQuestion,
        answer: nextQuestion.type === "multiQuestion"
            ? Array.from({ length: nextQuestion.repeatCount || 1 }, () =>
                Array(nextQuestion.subQuestions?.length || 0).fill("")
            )
            : nextQuestion.type === "multiSelect"
                ? []
                : "",
    };
};

/**
 * Trims the quiz to only include answered questions up to the current index.
 */
export const trimQuestionsAfter = (
    currentIndex: number,
    questions: QuestionProps[]
): QuestionProps[] => {
    return questions.slice(0, currentIndex + 1);
};

/**
 * Called when a user answers a main question (radio, dropdown, etc.).
 * Updates state, applies logic, and appends next valid question (if any).
 */
const handleQuestionChange = (
    value: any,
    index: number,
    questions: QuestionProps[],
    setQuestions: React.Dispatch<React.SetStateAction<QuestionProps[]>>
) => {
    const updated = trimQuestionsAfter(index, [...questions]);
    updated[index].answer = value;

    const current = updated[index];
    const nextIndex = getNextIndexFromLogic(current.logic, value, updated);
    const nextQuestion = nextIndex ? getNextQuestionFromSchema(nextIndex, LeadQuizFlowQuestions) : null;

    if (nextQuestion) {
        updated.push(nextQuestion);
    }

    setQuestions(updated);
};

/**
 * Called when a user answers a sub-question (in multiQuestion).
 * Updates state, applies logic, and appends next valid question (if any).
 */
const handleSubQuestionChange = (
    repeatIndex: number,
    subIndex: number,
    value: any,
    index: number,
    questions: QuestionProps[],
    setQuestions: React.Dispatch<React.SetStateAction<QuestionProps[]>>
) => {
    const updated = trimQuestionsAfter(index, [...questions]);

    if (!updated[index].answer) {
        updated[index].answer = [];
    }
    if (!updated[index].answer[repeatIndex]) {
        updated[index].answer[repeatIndex] = [];
    }

    updated[index].answer[repeatIndex][subIndex] = value;

    const current = updated[index];
    const nextIndex = getNextIndexFromLogic(current.logic, current.answer, updated);
    const nextQuestion = nextIndex ? getNextQuestionFromSchema(nextIndex, LeadQuizFlowQuestions) : null;

    if (nextQuestion) {
        updated.push(nextQuestion);
    }

    setQuestions(updated);
};

export const quizEngine = {
    handleQuestionChange,
    handleSubQuestionChange,
    OMIT_QUESTIONS
};